import { validateConditionalRules } from 'dashboard/lib/workflows/validation/validateConditionalStep';
import { WorkflowValidationContext } from 'dashboard/lib/workflows/validation/validationContext';
import { BaseTrigger } from 'dashboard/models/trigger';
import { WorkflowConfigError, WorkflowErrorType } from 'dashboard/models/workflow';
import {
  INPUT_STEP_TOKEN,
  WORKFLOW_WAITABLE_REPEATABLE_EVENT,
  WorkflowStepWait,
} from 'dashboard/models/workflow-version';
import { findPath } from 'lib/workflows';

export const validateWaitStep = (
  step: WorkflowStepWait,
  validationContext: WorkflowValidationContext,
): WorkflowConfigError[] => {
  const errors: WorkflowConfigError[] = [];

  if (step['watched-events'] != null) {
    // Wait for 'Object'
    if (step['watched-events'].length === 0) {
      errors.push({
        token: step.token,
        field: 'watched-events',
        message: 'Should be selected',
        type: WorkflowErrorType.Publish,
      });
    } else {
      // If we have some events ensure
      // * valid event name
      // * the step token is in the workflow tree or its the trigger
      step['watched-events'].forEach((event) => {
        const inputTokens = validationContext.inputStep.inputs.map(
          ({ token }) => `${INPUT_STEP_TOKEN}.${token}`,
        );
        if (event['event-name'] == null) {
          errors.push({
            token: step.token,
            field: 'watched-events',
            message: 'Should be selected',
            type: WorkflowErrorType.Publish,
          });
        } else if (
          !(
            findPath(step.token, validationContext.stepsConfig).some(
              ({ token }) => token === event['event-object-step-token'],
            ) || [BaseTrigger, ...inputTokens].includes(event['event-object-step-token'])
          )
        ) {
          errors.push({
            token: step.token,
            field: 'watched-events',
            message: `${event['event-name']} is not found until this step`,
            type: WorkflowErrorType.Publish,
          });
        }
      });
    }
  }

  if (
    step['watched-events'] != null &&
    step['conditional'] != null &&
    step['conditional'].rules.or.length > 0
  ) {
    if (step['watched-events'].length === 0) {
      errors.push({
        token: step.token,
        field: 'conditional',
        message: 'Event(s) must be selected for the condition to apply',
        type: WorkflowErrorType.Publish,
      });
    }

    if (
      step['watched-events'].length === 1 &&
      step['watched-events'][0]['event-name'] &&
      !WORKFLOW_WAITABLE_REPEATABLE_EVENT.includes(step['watched-events'][0]['event-name'])
    ) {
      errors.push({
        token: step.token,
        field: 'conditional',
        message:
          'Additional condition is only allowed if waiting for multiple events or one repeatable event',
        type: WorkflowErrorType.Publish,
      });
    }
    const { jsonSchema } = validationContext.getContextForStep(step.token);
    errors.push(
      ...validateConditionalRules(step.conditional, validationContext, step.token, jsonSchema),
    );
  }

  const waitPeriodSeconds =
    typeof step['wait-period-seconds'] === 'number' ? step['wait-period-seconds'] : 0;
  if (waitPeriodSeconds < 60) {
    errors.push({
      token: step.token,
      field: 'wait-period-seconds',
      message:
        'Specify a timeout between 60 seconds and 2592000 seconds (30 days). All wait steps require a default resolution time.',
      type: WorkflowErrorType.Publish,
    });
  }

  if (waitPeriodSeconds > 30 * 24 * 60 * 60) {
    errors.push({
      token: step.token,
      field: 'wait-period-seconds',
      message: 'Must be less than 30 days',
      type: WorkflowErrorType.Publish,
    });
  }

  return errors;
};
